/**
 * FoldedSpacePassiveSkillEffect — Astral rift-path passive: Phase Step cooldown
 * refund on Implosion collapse with 2+ enemies.
 *
 * When this passive unlocks, it registers a CooldownHook that fires when
 * ImplosionSkillEffect notifies the 'implosion_collapse_2plus' condition
 * (collapse with 2 or more enemies inside the pull radius). The hook refunds
 * 30% of Phase Step's total cooldown (subject to the 100ms global minimum).
 *
 * This is a Fork 2 gate for the Rift path: when it unlocks, the fork UI
 * presents the choice between Event Horizon and Anchor sub-paths. The fork
 * gate logic lives in SkillProgressionComponent, not here.
 *
 * Implementation note: the hook targets 'blink' (Phase Step inherits blink's
 * cooldown slot via the replacesId chain). The ImplosionSkillEffect.collapse()
 * method fires the condition; this passive's constructor registers the hook
 * that responds to it.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § rift_path_passive
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { CooldownHook } from '../../../systems/CooldownHookSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface FoldedSpaceDeps {
  /** CooldownHookSystem — hook is registered at unlock time (constructor). */
  readonly cooldownHooks: {
    register(hook: CooldownHook): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface FoldedSpaceConfig {
  /** Enemies inside pull radius required to trigger the condition. */
  readonly enemyCountThreshold: number;
  /** Fraction of Phase Step cooldown refunded on qualifying collapse. */
  readonly phaseStepCooldownReduction: number;
}

const DEFAULT_CONFIG: FoldedSpaceConfig = {
  enemyCountThreshold: 2,
  phaseStepCooldownReduction: 0.3,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class FoldedSpacePassiveSkillEffect implements SkillEffect {
  readonly skillId = 'rift_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  readonly config: FoldedSpaceConfig;

  constructor(deps: FoldedSpaceDeps, config: Partial<FoldedSpaceConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Register cooldown hook at unlock time.
    deps.cooldownHooks.register({
      id: 'folded_space_hook',
      trigger: { type: 'onCondition', conditionId: 'implosion_collapse_2plus' },
      targetSkillId: 'blink', // phase_step inherits blink's cooldown slot
      effect: { type: 'refundPercent', percent: this.config.phaseStepCooldownReduction },
    });
  }

  // --- SkillEffect interface ---

  activate(): boolean { return false; }
  isActive(): boolean { return false; }
  update(_delta: number): void { /* passive — nothing to tick */ }
  destroy(): void { /* hook unregistration handled by SkillProgressionComponent */ }
}
